"use client";
import logo from "@/assets/images/logo.svg";
import Image, { StaticImageData } from "next/image";
import dynamic from "next/dynamic";
import NavLinks, { navLinksList } from "./NavLinks";
import { FaChevronLeft } from "react-icons/fa6";
import { Cross as Hamburger } from "hamburger-react";
import Link from "next/link";
import { useMemo, useState } from "react";
import NavBottom from "./NavBottom";
import styles from "./navbar.module.css";
import AllProductsNav from "./AllProductsNav";
import AllSolutionsNav from "./AllSolutionsNav";
import AllKrooloAiNav from "./AllKrooloAiNav";
import {
  BookDemoHubspotButtonUrl,
  LoginHubspotButtonUrl,
  SignUpHubspotButtonUrl,
} from "@/utils/hubspot-contants";
import { usePathname } from "next/navigation";
import CustomButton from "@/components/common/CustomButton";

const BlogNavBar = dynamic(() => import("./BlogNavBar"));

interface ProductLink {
  icon?: string | StaticImageData;
  label?: string;
}

const Navbar = () => {
  const pathname = usePathname();
  const [hoveredArea, setHoveredArea] = useState<string>("");
  const [expandableArea, setExpandableArea] = useState<number>();
  const [forceRenderValue, setForceRenderValue] = useState<number>(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<string | null>(null);

  const handleLinkClick = (linkTitle: string) => {
    setSelectedLink((prevLink) => (prevLink === linkTitle ? null : linkTitle));
  };

  const isBlogPage = pathname?.startsWith("/blog");

  const {
    selectedItem,
    links,
    allProductLinks,
    integrationLinks,
    krooloLinks,
    aiToolsLinks,
    industriesLinks,
    compareLinks,
    solutionLinks,
    krooloAiLinks,
  } = useMemo(() => {
    const selectedItem = navLinksList.find(
      (item) => item.title === selectedLink,
    );
    return {
      selectedItem,
      links: selectedItem?.innerLinks || [],
      allProductLinks: selectedItem?.allProductLinks || [],
      integrationLinks: selectedItem?.integrationLinks || [],
      krooloLinks: selectedItem?.krooloLinks || [],
      aiToolsLinks: selectedItem?.aiToolsLinks || [],
      industriesLinks: selectedItem?.industriesLinks || [],
      compareLinks: selectedItem?.compareLinks || [],
      solutionLinks: selectedItem?.solutionLinks || [],
      krooloAiLinks: selectedItem?.krooloAiLinks || [],
    };
  }, [selectedLink]);

  // If it's a blog page, render the BlogNavBar
  if (isBlogPage) {
    return <BlogNavBar />;
  }

  return (
    <>
      <header className="fixed left-0 top-[6px] z-[500] w-full">
        {/* desktop navbar */}
        <div className="navbar--container container relative z-40 hidden max-w-[1288px] xl:block">
          <div
            style={{
              height: expandableArea ? `${expandableArea + 80}px` : "65px",
            }}
            className={`px-6 py-2 pt-0 ${expandableArea
              ? "bg-[#FAFAFA]"
              : "bg-[#ffffffa8] backdrop-blur-[24px]"
              } flex h-[58px] flex-col overflow-hidden rounded-[8px] duration-500 ease-in-out`}
          >
            <div className="flex items-center justify-between">
              <Link href={"/"} className="block w-[160px]">
                <Image
                  src={logo}
                  quality={100}
                  alt="Kroolo - AI-powered productivity platform"
                  className="object-contain"
                  draggable={false}
                  priority
                />
              </Link>
              <NavLinks
                setHoveredArea={setHoveredArea}
                setExpandableArea={setExpandableArea}
                hoveredArea={hoveredArea}
                setForceRenderValue={setForceRenderValue}
              />
              <div className="flex items-center gap-4">
                <CustomButton
                  text="Book Demo"
                  url={BookDemoHubspotButtonUrl}
                  variant="secondary"
                  version="black"
                />
                <div className="flex items-center gap-4">
                  <CustomButton
                    text="Login"
                    url={LoginHubspotButtonUrl}
                    variant="secondary"
                    version="black"
                  />
                  <CustomButton
                    text="Sign Up"
                    url={SignUpHubspotButtonUrl}
                    variant="primary"
                    version="black"
                  />
                </div>
              </div>
            </div>
            <NavBottom
              hoveredArea={hoveredArea}
              setHoveredArea={setHoveredArea}
              setExpandableArea={setExpandableArea}
              forceRenderValue={forceRenderValue}
            />
          </div>
        </div>
        <div className="container relative z-40 block xl:hidden">
          <div
            className={`${mobileNavOpen ? "h-[calc(100dvh-25px)] overflow-y-auto bg-[#FAFAFA]" : "h-[58px] overflow-hidden bg-[#ffffffa8]"} rounded-lg border-[0.25px] border-[#535252] px-3 py-1 backdrop-blur-[24px] duration-300 ease-in-out md:px-5 ${styles.mobile_nav_holder} `}
          >
            <div className="flex items-center justify-between">
              <Link href={"/"} className="block h-[40px] w-[100px] md:w-[90px]">
                {" "}
                <Image
                  src={logo}
                  quality={100}
                  alt="Kroolo - AI-powered productivity platform"
                  className="h-full w-full object-contain"
                  onClick={() => setMobileNavOpen(false)}
                />{" "}
              </Link>
              <div className="hidden items-center gap-1 md:flex">
                <div className="flex items-center gap-6">
                  <div className="hidden md:flex">
                    <CustomButton
                      text="Book Demo"
                      url={BookDemoHubspotButtonUrl}
                      variant="secondary"
                      version="black"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <CustomButton
                      text="Login"
                      url={LoginHubspotButtonUrl}
                      variant="secondary"
                      version="black"
                    />
                    <CustomButton
                      text="Sign Up"
                      url={SignUpHubspotButtonUrl}
                      variant="primary"
                      version="black"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <Hamburger
                    toggle={setMobileNavOpen}
                    toggled={mobileNavOpen}
                    size={16}
                    rounded
                    direction="right"
                    color="#202020"
                    label={
                      mobileNavOpen
                        ? "Close mobile navigation menu"
                        : "Open mobile navigation menu"
                    }
                  />
                </div>
              </div>
              <div className="flex items-center justify-center md:hidden">
                <Hamburger
                  toggle={setMobileNavOpen}
                  toggled={mobileNavOpen}
                  size={16}
                  rounded
                  direction="right"
                  color="#202020"
                  label={
                    mobileNavOpen
                      ? "Close mobile navigation menu"
                      : "Open mobile navigation menu"
                  }
                />
              </div>
            </div>
            <div className="flex w-full items-center justify-between border-b-[0.25px] border-[#535252] py-[8px] md:hidden">
              <div className="flex items-center max-[400px]:w-full max-[400px]:justify-between min-[400px]:gap-2">
                <CustomButton
                  text="Login"
                  url={LoginHubspotButtonUrl}
                  variant="secondary"
                  version="black"
                />
                <CustomButton
                  text="Sign Up"
                  url={SignUpHubspotButtonUrl}
                  variant="primary"
                  version="black"
                />
              </div>
              <div className="flex max-[400px]:hidden">
                <CustomButton
                  text="Book Demo"
                  url={BookDemoHubspotButtonUrl}
                  variant="secondary"
                  version="black"
                />
              </div>
            </div>
            {selectedLink ? (
              <>
                <button
                  onClick={() => setSelectedLink(null)}
                  className="flex w-full items-center gap-2 border-b-[0.25px] border-[#535252] py-4 text-sm font-medium text-[#202020]"
                >
                  <FaChevronLeft size={16} />
                  Back
                </button>
                {selectedLink === "Products" ? (
                  <AllProductsNav
                    krooloLinks={krooloLinks}
                    allProductLinks={allProductLinks}
                    integrationLinks={integrationLinks}
                    compareLinks={compareLinks}
                    krooloAiLinks={krooloAiLinks}
                    aiToolsLinks={aiToolsLinks}
                  />
                ) : selectedLink === "Kroolo AI" ? (
                  <AllKrooloAiNav
                    krooloAiLinks={krooloAiLinks}
                    aiToolsLinks={aiToolsLinks}
                  />
                ) : selectedLink === "Solutions" ? (
                  <AllSolutionsNav
                    solutionLinks={solutionLinks}
                    industriesLinks={industriesLinks}
                  />
                ) : (
                  <div className="mb-16 mt-4 h-[calc(100dvh-258px)] overflow-y-auto text-white">
                    {selectedItem &&
                      links?.map((link, idx) => (
                        <a
                          href={link.url}
                          rel="noreferrer"
                          key={idx}
                          className="mb-2 flex items-center gap-2 rounded-lg bg-[#F1F1F1] p-[10px] text-[#202020] backdrop-blur-[24px]"
                        >
                          <Image
                            src={link.icon || ""}
                            alt={link.label || ""}
                            width={24}
                            height={24}
                          />
                          <p className="text-sm font-normal">{link.label}</p>
                        </a>
                      ))}
                  </div>
                )}
              </>
            ) : (
              <ul className="mt-4 items-start gap-2 gap-y-3 md:grid md:grid-cols-2">
                {navLinksList
                  .filter(
                    (navItem) =>
                      navItem.innerLinks ||
                      navItem.allProductLinks ||
                      navItem.integrationLinks ||
                      navItem.aiToolsLinks ||
                      navItem.industriesLinks ||
                      navItem.solutionLinks ||
                      navItem.krooloLinks ||
                      navItem.krooloAiLinks,
                  )
                  .map((item, index) => (
                    <li key={index} className="rounded">
                      <button
                        onClick={() => handleLinkClick(item.title)}
                        className="group flex w-full items-center justify-between rounded-lg px-[10px] py-[8px] text-left text-base font-medium text-[#202020] hover:bg-[#EDEDED] hover:backdrop-blur-[24px]"
                      >
                        <span className="text-[14px] font-medium transition-all duration-200">
                          {item.title}
                        </span>
                        <svg
                          width="8"
                          height="14"
                          viewBox="0 0 8 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 1.5L6 7L1 12.5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            className="transition-all duration-200"
                          />
                        </svg>
                      </button>
                    </li>
                  ))}
                {navLinksList
                  .filter(
                    (navItem) =>
                      !(
                        navItem.innerLinks ||
                        navItem.allProductLinks ||
                        navItem.integrationLinks ||
                        navItem.aiToolsLinks ||
                        navItem.industriesLinks ||
                        navItem.solutionLinks ||
                        navItem.krooloLinks ||
                        navItem.krooloAiLinks
                      ),
                  )
                  .map((item, index) => (
                    <li key={index} className="rounded">
                      <a
                        href={item.targetUrl}
                        rel="noreferrer"
                        className="flex w-full items-center justify-between rounded-lg px-[10px] py-[8px] text-left text-base font-medium text-[#202020] hover:bg-[#EDEDED] hover:backdrop-blur-[24px]"
                      >
                        <span className="text-[14px] font-medium transition-all duration-200">
                          {item.title}
                        </span>
                      </a>
                    </li>
                  ))}
              </ul>
            )}
          </div>
          <div
            className={`${mobileNavOpen ? "absolute bottom-4 ml-6 flex justify-center gap-4 min-[400px]:ml-8" : "hidden"}`}
          >
            <CustomButton
              text="Watch Videos"
              url="/videos"
              variant="secondary"
              version="black"
            />
            <CustomButton
              text="Book Demo"
              url={BookDemoHubspotButtonUrl}
              variant="primary"
              version="black"
            />
          </div>
        </div>
      </header>
      <div className="navbar--container--blur h-[70px]"></div>
      <div
        className={`fixed left-0 top-0 z-[400] hidden h-full w-full bg-[rgba(16,18,20,0.14)] backdrop-blur-[10px] duration-300 ease-in-out lg:block ${expandableArea ? "visible opacity-100" : "invisible opacity-0"
          } `}
      ></div>
    </>
  );
};

export default Navbar;
